import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/nocode';
import { TABLES } from '../../api/tables';
import { useAcademy } from '../../context/AcademyContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import CloneRun from '../../components/CloneRun';

const EMPTY_CENTER = { name: '', slug: '', description: '' };
const EMPTY_SESSION = {
  department_id: '',
  title: '',
  slug: '',
  description: '',
  featured_image: '',
  skillplate_student_product: '',
  skillplate_allaccess_product: ''
};
const EMPTY_RUN = {
  session_id: '',
  title: '',
  slug: '',
  slugTouched: false,
  start_date: '',
  end_date: '',
  skillplate_student_product: '',
  skillplate_allaccess_product: '',
  skillplate_products: [
    { pass_type: 'paid', product_name: '' },
    { pass_type: 'allaccess', product_name: '' }
  ]
};

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeProduct(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseRunProducts(run = {}) {
  try {
    const products = JSON.parse(run.skillplate_products || '[]');

    if (Array.isArray(products) && products.length) {
      return products.filter((product) => (
        ['paid', 'allaccess'].includes(product?.pass_type) &&
        String(product?.product_name || '').trim()
      ));
    }
  } catch {
    // Use legacy product columns below.
  }

  return [
    {
      pass_type: 'paid',
      product_name: run.skillplate_paid_product || run.skillplate_student_product || ''
    },
    {
      pass_type: 'allaccess',
      product_name: run.skillplate_allaccess_product || ''
    }
  ];
}

function normalizeRunProducts(products) {
  return products
    .map((product) => ({
      pass_type: product.pass_type,
      product_name: String(product.product_name || '').trim()
    }))
    .filter((product) => product.product_name);
}

function validRunProducts(products) {
  const values = normalizeRunProducts(products);
  const paid = values.filter((product) => product.pass_type === 'paid');
  const allaccess = values.filter((product) => product.pass_type === 'allaccess');
  const names = values.map((product) => normalizeProduct(product.product_name));

  return (
    paid.length > 0 &&
    allaccess.length > 0 &&
    names.length === new Set(names).size
  );
}

function ProductFields({ products, onChange }) {
  const updateProduct = (index, productName) => {
    onChange(products.map((product, productIndex) => (
      productIndex === index
        ? { ...product, product_name: productName }
        : product
    )));
  };

  const addProduct = (passType) => {
    onChange([
      ...products,
      { pass_type: passType, product_name: '' }
    ]);
  };

  const removeProduct = (index) => {
    onChange(products.filter((_, productIndex) => productIndex !== index));
  };

  const renderProducts = (passType, label) => {
    const items = products
      .map((product, index) => ({ ...product, index }))
      .filter((product) => product.pass_type === passType);

    return (
      <div className="space-y-3">
        {items.map((product, position) => (
          <div key={`${passType}-${product.index}`} className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label={position === 0 ? label : `${label} ${position + 1}`}
                value={product.product_name}
                onChange={(event) => updateProduct(product.index, event.target.value)}
                placeholder="Exact product name from Skillplate"
              />
            </div>
            {position > 0 && (
              <button
                type="button"
                className="mb-1 rounded-md border border-tan px-3 py-2 text-muted hover:border-red-400 hover:text-red-600"
                onClick={() => removeProduct(product.index)}
                aria-label={`Remove ${label}`}
              >
                <SafeIcon name="X" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => addProduct(passType)}
          className="flex items-center gap-1 text-sm font-medium text-dark-green hover:text-forest-green"
        >
          <SafeIcon name="Plus" />
          Add another {passType === 'paid' ? 'Student Access' : 'All-Access'} Product
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4 rounded-lg border border-amber/40 bg-amber/10 p-4">
      <div>
        <h3 className="font-bold text-dark-green">Skillplate Product Assignment</h3>
        <p className="mt-1 text-xs text-muted">
          Each product name maps a Skillplate purchase to this event run and its access level.
        </p>
      </div>
      {renderProducts('paid', 'Skillplate Student Access Product')}
      {renderProducts('allaccess', 'Skillplate All-Access Product')}
    </div>
  );
}

function CenterForm({ form, setForm, saving, onSave }) {
  return (
    <Card className="space-y-4">
      <h2 className="text-xl font-bold text-dark-green">
        1. New Learning Center
      </h2>
      <Input
        label="Center name"
        value={form.name}
        onChange={(event) => setForm({
          ...form,
          name: event.target.value,
          slug: slugify(event.target.value)
        })}
        placeholder="Writing Center"
      />
      <Input
        label="Slug"
        value={form.slug}
        onChange={(event) => setForm({ ...form, slug: event.target.value })}
        placeholder="writing"
      />
      <Input
        label="Description"
        value={form.description}
        onChange={(event) => setForm({
          ...form,
          description: event.target.value
        })}
      />
      <Button
        className="w-full"
        disabled={!form.name || saving}
        onClick={onSave}
      >
        <SafeIcon name="Plus" /> Add Center
      </Button>
    </Card>
  );
}

function SessionForm({ form, setForm, departments, saving, onSave }) {
  return (
    <Card className="space-y-4">
      <h2 className="text-xl font-bold text-dark-green">2. New Session</h2>
      <label className="flex flex-col gap-1 text-sm font-medium text-dark-green">
        Learning center
        <select
          className="rounded-md border border-amber bg-cream px-4 py-2"
          value={form.department_id}
          onChange={(event) => setForm({
            ...form,
            department_id: event.target.value
          })}
        >
          <option value="">Choose a center</option>
          {departments.map((item) => (
            <option key={item.id} value={item.id}>{item.name}</option>
          ))}
        </select>
      </label>
      <Input
        label="Session name"
        value={form.title}
        onChange={(event) => setForm({
          ...form,
          title: event.target.value,
          slug: slugify(event.target.value)
        })}
        placeholder="StoryWise Framework"
      />
      <Input
        label="Slug"
        value={form.slug}
        onChange={(event) => setForm({ ...form, slug: event.target.value })}
      />
      <Input
        label="Description"
        value={form.description}
        onChange={(event) => setForm({
          ...form,
          description: event.target.value
        })}
      />
      <Input
        label="Session Featured Image (URL)"
        value={form.featured_image}
        onChange={(event) => setForm({
          ...form,
          featured_image: event.target.value
        })}
        placeholder="https://images.unsplash.com/..."
      />
      <Button
        className="w-full"
        disabled={
          !form.department_id ||
          !form.title ||
          saving
        }
        onClick={onSave}
      >
        <SafeIcon name="Plus" /> Add Session
      </Button>
    </Card>
  );
}

function RunForm({ form, setForm, sessions, saving, onSave }) {
  const handleTitleChange = (event) => {
    const title = event.target.value;
    const autoSlug = slugify(title);
    setForm((prev) => ({
      ...prev,
      title,
      slug: prev.slugTouched ? prev.slug : autoSlug
    }));
  };

  const handleSlugChange = (event) => {
    setForm((prev) => ({
      ...prev,
      slug: slugify(event.target.value),
      slugTouched: true
    }));
  };

  const previewSlug = form.slug || slugify(form.title) || 'custom-run-slug';

  return (
    <Card className="space-y-4">
      <h2 className="text-xl font-bold text-dark-green">
        3. Launch Event Run
      </h2>
      <label className="flex flex-col gap-1 text-sm font-medium text-dark-green">
        Session
        <select
          className="rounded-md border border-amber bg-cream px-4 py-2"
          value={form.session_id}
          onChange={(event) => setForm({
            ...form,
            session_id: event.target.value
          })}
        >
          <option value="">Choose a session</option>
          {sessions.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title || item.name}
            </option>
          ))}
        </select>
      </label>
      <Input
        label="Run name"
        value={form.title}
        onChange={handleTitleChange}
        placeholder="Fall 2026"
      />
      <div>
        <Input
          label="Run URL Slug"
          value={form.slug}
          onChange={handleSlugChange}
          placeholder="fall-2026"
        />
        <p className="mt-1 text-xs text-muted">
          Student URL: <span className="font-mono font-semibold text-dark-green">#/hub/{previewSlug}</span>
        </p>
      </div>
      <Input
        label="Start date"
        type="date"
        value={form.start_date}
        onChange={(event) => setForm({
          ...form,
          start_date: event.target.value
        })}
      />
      <Input
        label="End date"
        type="date"
        value={form.end_date}
        onChange={(event) => setForm({
          ...form,
          end_date: event.target.value
        })}
      />
      <ProductFields
        products={form.skillplate_products}
        onChange={(skillplateProducts) => setForm({
          ...form,
          skillplate_products: skillplateProducts
        })}
      />
      <Button
        className="w-full"
        disabled={
          !form.session_id ||
          !form.title ||
          !form.start_date ||
          !form.end_date ||
          !validRunProducts(form.skillplate_products) ||
          saving
        }
        onClick={onSave}
      >
        <SafeIcon name="Zap" /> Launch Run
      </Button>
    </Card>
  );
}

function EditCenterModal({ center, onClose, onSave, saving }) {
  const [formData, setFormData] = useState({
    name: center?.name || '',
    slug: center?.slug || '',
    description: center?.description || ''
  });

  if (!center) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <Card className="w-full max-w-lg space-y-4 border-2 border-dark-green shadow-2xl">
        <div className="flex items-center justify-between border-b border-tan pb-3">
          <h3 className="text-xl font-bold text-dark-green">
            Edit Learning Center
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-dark-green"
          >
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>
        <Input
          label="Center name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          label="Slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
        />
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(center.id, formData)}
            disabled={!formData.name || saving}
          >
            <SafeIcon name="Save" /> {saving ? 'Saving...' : 'Save Center'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function EditSessionModal({ session, departments, sessions, onClose, onSave, saving, setMessage }) {
  const [formData, setFormData] = useState({
    department_id: session?.department_id || '',
    title: session?.title || session?.name || '',
    slug: session?.slug || '',
    description: session?.description || '',
    featured_image: session?.featured_image || '',
    skillplate_student_product: session?.skillplate_student_product || '',
    skillplate_allaccess_product: session?.skillplate_allaccess_product || ''
  });

  if (!session) return null;

  const handleSave = () => {
    const {
      skillplate_student_product,
      skillplate_allaccess_product,
      ...sessionData
    } = formData;
    onSave(session.id, sessionData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <Card className="my-8 w-full max-w-xl space-y-4 border-2 border-dark-green shadow-2xl">
        <div className="flex items-center justify-between border-b border-tan pb-3">
          <h3 className="text-xl font-bold text-dark-green">
            Edit Session
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-dark-green"
          >
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>
        <label className="flex flex-col gap-1 text-sm font-medium text-dark-green">
          Learning center
          <select
            className="rounded-md border border-amber bg-cream px-4 py-2"
            value={formData.department_id}
            onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
          >
            <option value="">Choose a center</option>
            {departments.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </label>
        <Input
          label="Session name"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <Input
          label="Slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
        />
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        <Input
          label="Session Featured Image (URL)"
          value={formData.featured_image}
          onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
          placeholder="https://images.unsplash.com/..."
        />
        <div className="flex justify-end gap-3 pt-2 border-t border-tan">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!formData.title || saving}>
            <SafeIcon name="Save" /> {saving ? 'Saving...' : 'Save Session'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function EditRunModal({ run, sessions, onClose, onSave, saving }) {
  const [formData, setFormData] = useState({
    session_id: run?.session_id || '',
    title: run?.title || run?.name || '',
    slug: run?.slug || slugify(run?.title || run?.name || ''),
    start_date: run?.start_date || '',
    end_date: run?.end_date || '',
    skillplate_student_product: run?.skillplate_paid_product || '',
    skillplate_allaccess_product: run?.skillplate_allaccess_product || '',
    skillplate_products: parseRunProducts(run)
  });

  if (!run) return null;

  const previewSlug = formData.slug || slugify(formData.title) || (run?.id ? `run-${run.id}` : 'custom-slug');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <Card className="my-8 w-full max-w-lg space-y-4 border-2 border-dark-green shadow-2xl">
        <div className="flex items-center justify-between border-b border-tan pb-3">
          <h3 className="text-xl font-bold text-dark-green">
            Edit Event Run
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-dark-green"
          >
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>
        <label className="flex flex-col gap-1 text-sm font-medium text-dark-green">
          Session
          <select
            className="rounded-md border border-amber bg-cream px-4 py-2"
            value={formData.session_id}
            onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
          >
            <option value="">Choose a session</option>
            {sessions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || item.name}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="Run name"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <div>
          <Input
            label="Run URL Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: slugify(e.target.value) })}
            placeholder="e.g. spring-2026"
          />
          <p className="mt-1 text-xs text-muted">
            Student URL: <span className="font-mono font-semibold text-dark-green">#/hub/{previewSlug}</span>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Start date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
          />
          <Input
            label="End date"
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          />
        </div>
        <ProductFields
          products={formData.skillplate_products}
          onChange={(skillplateProducts) => setFormData({
            ...formData,
            skillplate_products: skillplateProducts
          })}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(run.id, {
              session_id: formData.session_id,
              title: formData.title,
              slug: slugify(formData.slug || formData.title),
              start_date: formData.start_date,
              end_date: formData.end_date,
              skillplate_paid_product: normalizeRunProducts(formData.skillplate_products)
                .find((product) => product.pass_type === 'paid')?.product_name || '',
              skillplate_allaccess_product: normalizeRunProducts(formData.skillplate_products)
                .find((product) => product.pass_type === 'allaccess')?.product_name || '',
              skillplate_products: JSON.stringify(
                normalizeRunProducts(formData.skillplate_products)
              )
            })}
            disabled={!formData.title || !validRunProducts(formData.skillplate_products) || saving}
          >
            <SafeIcon name="Save" /> {saving ? 'Saving...' : 'Save Run'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProductConfig({ session, sessions, onSaved, setMessage }) {
  const [studentProduct, setStudentProduct] = useState('');
  const [allAccessProduct, setAllAccessProduct] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStudentProduct(session.skillplate_student_product || '');
    setAllAccessProduct(session.skillplate_allaccess_product || '');
  }, [session]);

  const saveProducts = async () => {
    const studentKey = normalizeProduct(studentProduct);
    const allAccessKey = normalizeProduct(allAccessProduct);

    if (!studentKey || !allAccessKey) {
      setMessage('Both Skillplate products are required for every session.');
      return;
    }

    if (studentKey === allAccessKey) {
      setMessage('Student and All-Access products must be different.');
      return;
    }

    const conflict = sessions.find((item) => {
      if (String(item.id) === String(session.id)) return false;
      const values = parseRunProducts(item)
        .map((product) => normalizeProduct(product.product_name));
      return values.includes(studentKey) || values.includes(allAccessKey);
    });

    if (conflict) {
      setMessage(
        `A product name is already assigned to ${conflict.title || conflict.name}.`
      );
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await api.put(TABLES.runs, session.id, {
        skillplate_paid_product: studentProduct.trim(),
        skillplate_allaccess_product: allAccessProduct.trim(),
        skillplate_products: JSON.stringify([
          { pass_type: 'paid', product_name: studentProduct.trim() },
          { pass_type: 'allaccess', product_name: allAccessProduct.trim() }
        ])
      });
      await onSaved();
      setMessage('Session Skillplate products saved.');
    } catch (error) {
      setMessage(error?.message || 'Unable to save product assignments.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 rounded-lg border border-amber/40 bg-amber/10 p-4">
      <div className="mb-3">
        <h4 className="font-bold text-dark-green">
          Skillplate Product Assignment
        </h4>
        <p className="mt-1 text-xs text-muted">
          Product names must exactly match the values sent by Skillplate.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Input
          label="Student Access Product"
          value={studentProduct}
          onChange={(event) => setStudentProduct(event.target.value)}
          placeholder="Exact Skillplate product name"
        />
        <Input
          label="All-Access Product"
          value={allAccessProduct}
          onChange={(event) => setAllAccessProduct(event.target.value)}
          placeholder="Exact Skillplate product name"
        />
      </div>
      <Button
        className="mt-3"
        variant="outline"
        disabled={saving}
        onClick={saveProducts}
      >
        <SafeIcon name="Save" />
        {saving ? 'Saving Products...' : 'Save Product Assignment'}
      </Button>
    </div>
  );
}

export default function Sessions() {
  const navigate = useNavigate();
  const {
    departments,
    sessions,
    runs,
    currentRun,
    selectRun,
    refreshAcademy,
    isEnabled,
    getRunSlug
  } = useAcademy();
  const [centerForm, setCenterForm] = useState(EMPTY_CENTER);
  const [sessionForm, setSessionForm] = useState(EMPTY_SESSION);
  const [runForm, setRunForm] = useState(EMPTY_RUN);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState('');

  // Modals for editing existing records
  const [editingCenter, setEditingCenter] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [editingRun, setEditingRun] = useState(null);
  const [cloningRun, setCloningRun] = useState(null);
  const [updating, setUpdating] = useState(false);

  const sessionsByDepartment = useMemo(() => {
    return departments.map((department) => ({
      department,
      sessions: sessions.filter(
        (session) =>
          String(session?.department_id ?? '') === String(department?.id)
      )
    }));
  }, [departments, sessions]);

  const save = async (type, table, payload, reset) => {
    setSaving(type);
    setMessage('');

    try {
      await api.post(table, { ...payload, is_active: 1, status: 'active' });
      reset();
      await refreshAcademy();
      setMessage(`${type} created successfully.`);
    } catch (error) {
      setMessage(error?.message || `Unable to create ${type.toLowerCase()}.`);
    } finally {
      setSaving('');
    }
  };

  const updateRecord = async (type, table, id, payload, close) => {
    setUpdating(true);
    setMessage('');

    try {
      await api.put(table, id, payload);
      close();
      await refreshAcademy();
      setMessage(`${type} updated successfully.`);
    } catch (error) {
      setMessage(error?.message || `Unable to update ${type.toLowerCase()}.`);
    } finally {
      setUpdating(false);
    }
  };

  const saveSession = async () => {
    const {
      skillplate_student_product,
      skillplate_allaccess_product,
      ...sessionData
    } = sessionForm;

    await save(
      'Session',
      TABLES.sessions,
      sessionData,
      () => setSessionForm(EMPTY_SESSION)
    );
  };

  const saveRun = async () => {
    const products = normalizeRunProducts(runForm.skillplate_products);

    if (!validRunProducts(products)) {
      setMessage('Add at least one unique Student Access and All-Access product.');
      return;
    }

    const configuredProducts = runs.flatMap((run) => (
      parseRunProducts(run).map((product) => normalizeProduct(product.product_name))
    ));
    const conflict = products.find((product) => (
      configuredProducts.includes(normalizeProduct(product.product_name))
    ));

    if (conflict) {
      setMessage(
        `A product name is already assigned to another event run: ${conflict.product_name}.`
      );
      return;
    }

    const finalSlug = slugify(runForm.slug || runForm.title);

    await save(
      'Event run',
      TABLES.runs,
      {
        session_id: runForm.session_id,
        title: runForm.title,
        slug: finalSlug,
        start_date: runForm.start_date,
        end_date: runForm.end_date,
        skillplate_paid_product: products.find(
          (product) => product.pass_type === 'paid'
        )?.product_name || '',
        skillplate_allaccess_product: products.find(
          (product) => product.pass_type === 'allaccess'
        )?.product_name || '',
        skillplate_products: JSON.stringify(products)
      },
      () => setRunForm(EMPTY_RUN)
    );
  };

  const toggle = async (table, record) => {
    const nextActive = isEnabled(record) ? 0 : 1;

    try {
      await api.put(table, record.id, {
        is_active: nextActive,
        status: nextActive ? 'active' : 'inactive'
      });
      await refreshAcademy();
    } catch (error) {
      setMessage(error?.message || 'Unable to update availability.');
    }
  };

  const manageRun = (run) => {
    selectRun(run);
    navigate('/admin/content');
  };

  return (
    <div className="max-w-6xl space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-dark-green">
          Learning Centers & Sessions
        </h1>
        <p className="mt-2 text-muted">
          Organize programs into learning centers, schedule reusable week-long
          event runs, and assign Skillplate products to each specific session.
        </p>
      </header>

      {message && (
        <div className="rounded-lg border border-tan bg-white px-4 py-3 text-dark-green">
          {message}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <CenterForm
          form={centerForm}
          setForm={setCenterForm}
          saving={saving === 'Learning center'}
          onSave={() => save(
            'Learning center',
            TABLES.departments,
            centerForm,
            () => setCenterForm(EMPTY_CENTER)
          )}
        />
        <SessionForm
          form={sessionForm}
          setForm={setSessionForm}
          departments={departments}
          saving={saving === 'Session'}
          onSave={saveSession}
        />
        <RunForm
          form={runForm}
          setForm={setRunForm}
          sessions={sessions}
          saving={saving === 'Event run'}
          onSave={saveRun}
        />
      </div>

      <section className="space-y-5">
        {sessionsByDepartment.map(({ department, sessions: centerSessions }) => (
          <Card key={department.id} className="overflow-hidden p-0">
            <div className="flex items-center justify-between bg-dark-green px-6 py-4 text-white">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-white">
                    {department.name}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setEditingCenter(department)}
                    className="rounded bg-white/20 px-2 py-0.5 text-xs text-white hover:bg-white/30"
                  >
                    <SafeIcon name="Edit" className="inline mr-1" /> Edit
                  </button>
                </div>
                <p className="text-sm text-white/70">
                  {department.description}
                </p>
              </div>
              <Button
                variant="outline"
                className={`border text-white ${
                  isEnabled(department)
                    ? 'border-emerald-400 bg-emerald-600 hover:bg-emerald-700'
                    : 'border-rose-400 bg-rose-700 hover:bg-rose-800'
                }`}
                onClick={() => toggle(TABLES.departments, department)}
              >
                {isEnabled(department) ? 'Center On' : 'Center Off'}
              </Button>
            </div>

            <div className="divide-y divide-tan">
              {centerSessions.map((session) => {
                const sessionRuns = runs.filter(
                  (run) => String(run.session_id) === String(session.id)
                );

                return (
                  <div key={session.id} className="p-6">
                    <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                      <div className="flex gap-4 items-start">
                        {session.featured_image && (
                          <img
                            src={session.featured_image}
                            alt={session.title || session.name}
                            className="h-16 w-24 rounded object-cover border border-tan shrink-0"
                          />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-dark-green">
                              {session.title || session.name}
                            </h3>
                            <button
                              type="button"
                              onClick={() => setEditingSession(session)}
                              className="rounded bg-gray-100 px-2 py-0.5 text-xs text-dark-green hover:bg-amber/20 border border-tan"
                            >
                              <SafeIcon name="Edit2" className="inline mr-1" /> Edit
                            </button>
                          </div>
                          <p className="text-sm text-muted">
                            Morning · Afternoon · Evening · Podcast
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className={`${
                          isEnabled(session)
                            ? 'border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white'
                            : 'border-rose-600 text-rose-700 hover:bg-rose-600 hover:text-white'
                        }`}
                        onClick={() => toggle(TABLES.sessions, session)}
                      >
                        <SafeIcon
                          name={isEnabled(session) ? 'Eye' : 'EyeOff'}
                        />
                        {isEnabled(session) ? 'Session On' : 'Session Off'}
                      </Button>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {sessionRuns.map((run) => (
                        <div
                          key={run.id}
                          className={`rounded-lg border p-4 ${
                            String(currentRun?.id) === String(run.id)
                              ? 'border-forest-green bg-[#EAF1E6]'
                              : 'border-tan'
                          }`}
                        >
                          <div className="flex justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="font-bold text-dark-green">
                                  {run.title || run.name}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setEditingRun(run)}
                                  className="text-muted hover:text-dark-green text-xs"
                                  title="Edit run"
                                >
                                  <SafeIcon name="Edit3" />
                                </button>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                                <span>{run.start_date || 'No start date'}{run.end_date ? ` — ${run.end_date}` : ''}</span>
                                <span className="text-tan">·</span>
                                <span className="font-mono text-dark-green bg-amber/20 px-1.5 py-0.5 rounded text-[11px]">
                                  /hub/{getRunSlug(run)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggle(TABLES.runs, run)}
                              className={`h-7 w-12 shrink-0 rounded-full p-1 transition-colors ${
                                isEnabled(run) ? 'bg-amber' : 'bg-gray-300'
                              }`}
                              aria-label={`Toggle ${run.title || run.name}`}
                            >
                              <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${
                                isEnabled(run) ? 'translate-x-5' : ''
                              }`} />
                            </button>
                          </div>

                          <ProductConfig
                            session={{
                              ...run,
                              skillplate_student_product: run.skillplate_paid_product
                            }}
                            sessions={runs.map((item) => ({
                              ...item,
                              skillplate_student_product: item.skillplate_paid_product
                            }))}
                            onSaved={refreshAcademy}
                            setMessage={setMessage}
                          />

                          <Button
                            className="mt-4 w-full"
                            variant={
                              String(currentRun?.id) === String(run.id)
                                ? 'secondary'
                                : 'outline'
                            }
                            onClick={() => manageRun(run)}
                          >
                            <SafeIcon name="Edit3" />
                            {String(currentRun?.id) === String(run.id)
                              ? 'Managing This Run'
                              : 'Manage Run Content'}
                          </Button>
                          <Button
                            className="mt-2 w-full"
                            variant="outline"
                            onClick={() => setCloningRun(run)}
                          >
                            <SafeIcon name="Copy" /> Clone Event Run
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </section>

      {editingCenter && (
        <EditCenterModal
          center={editingCenter}
          onClose={() => setEditingCenter(null)}
          onSave={(id, data) => updateRecord('Learning Center', TABLES.departments, id, data, () => setEditingCenter(null))}
          saving={updating}
        />
      )}

      {editingSession && (
        <EditSessionModal
          session={editingSession}
          departments={departments}
          sessions={sessions}
          onClose={() => setEditingSession(null)}
          onSave={(id, data) => updateRecord('Session', TABLES.sessions, id, data, () => setEditingSession(null))}
          saving={updating}
          setMessage={setMessage}
        />
      )}

      {editingRun && (
        <EditRunModal
          run={editingRun}
          sessions={sessions}
          onClose={() => setEditingRun(null)}
          onSave={(id, data) => updateRecord('Event Run', TABLES.runs, id, data, () => setEditingRun(null))}
          saving={updating}
        />
      )}
      {cloningRun && (
        <CloneRun
          run={cloningRun}
          runs={runs}
          sessions={sessions}
          onClose={() => setCloningRun(null)}
          onCloned={async () => {
            await refreshAcademy();
            setMessage('Event run cloned. Assign new Skillplate products and review content before activating it.');
          }}
        />
      )}
    </div>
  );
}